import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ClubCard from './ClubCard';
import CreateClubModal from './CreateClubModal';
import ClubDetailPage from './ClubDetailPage';
import './ClubsPage.css';

const ClubsPage = () => {
  const { authFetch } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [inviteCode, setInviteCode] = useState('');
  const [joinError, setJoinError] = useState('');

  const loadClubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/clubs');
      if (res.ok) {
        const data = await res.json();
        setClubs(data.clubs || []);
      }
    } catch (err) {
      console.error('Failed to load clubs:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadClubs();
  }, [loadClubs]);

  const handleJoinByCode = async () => {
    setJoinError('');
    try {
      const res = await authFetch('/api/clubs/join', {
        method: 'POST',
        body: JSON.stringify({ invite_code: inviteCode })
      });

      if (res.ok) {
        setShowJoinModal(false);
        setInviteCode('');
        loadClubs();
      } else {
        const data = await res.json();
        setJoinError(data.detail || 'Invalid invite code');
      }
    } catch (err) {
      setJoinError('Failed to join club');
    }
  };

  const handleLeaveClub = async (clubId) => {
    try {
      const res = await authFetch(`/api/clubs/${clubId}/leave`, {
        method: 'POST'
      });

      if (res.ok) {
        loadClubs();
      }
    } catch (err) {
      console.error('Failed to leave club:', err);
    }
  };

  const handleClubCreated = () => {
    setShowCreateModal(false);
    loadClubs();
  };

  if (selectedClub) {
    return (
      <ClubDetailPage
        clubId={selectedClub}
        onBack={() => setSelectedClub(null)}
        onLeave={() => {
          setSelectedClub(null);
          loadClubs();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="clubs-page">
        <div className="clubs-loading">Loading clubs...</div>
      </div>
    );
  }

  return (
    <div className="clubs-page">
      <div className="clubs-container">
        <div className="clubs-header">
          <div className="clubs-header__left">
            <h1>Clubs/Groups</h1>
            <p>Join running clubs and train together</p>
          </div>
          <div className="clubs-header__actions">
            <button
              className="btn-secondary"
              onClick={() => setShowJoinModal(true)}
            >
              Join by Code
            </button>
            <button
              className="btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              Create Club/Group
            </button>
          </div>
        </div>

        {clubs.length === 0 ? (
          <div className="clubs-empty">
            <div className="clubs-empty__icon">👥</div>
            <h3>No Clubs/Groups Yet</h3>
            <p>Create a club or group to train together</p>
          </div>
        ) : (
          <div className="clubs-grid">
            {clubs.map(club => (
              <ClubCard
                key={club.id}
                club={club}
                onView={() => setSelectedClub(club.id)}
                onLeave={() => handleLeaveClub(club.id)}
              />
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateClubModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleClubCreated}
        />
      )}

      {showJoinModal && (
        <div className="modal-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Join Club</h2>
              <button className="modal-close" onClick={() => setShowJoinModal(false)}>&times;</button>
            </div>

            <div className="join-form">
              {joinError && <div className="modal-error">{joinError}</div>}
              <div className="form-group">
                <label>Invite Code</label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Enter invite code"
                />
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setShowJoinModal(false)}>
                  Cancel
                </button>
                <button className="btn-submit" onClick={handleJoinByCode}>
                  Join
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubsPage;
